import { observer } from "mobx-react-lite";
import React, { useContext, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

import CollapsableImageContent from "../components/CollapsableImageContent";
import FullscreenModal from "../components/FullscreenModal";
import InclusionDetail from "../components/InclusionDetail";
import { NavigationContext, UserContext } from "../contexts";
import { getPageTitle } from "../resources/routes";
import { Inclusion } from "../types";
import { scrollToTop } from "../util";

const PersonaliseHomeScreen = observer(() => {
  const userContext = useContext(UserContext);
  const navContext = useContext(NavigationContext);
  const location = useLocation();

  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isModalClosing, setIsModalClosing] = useState(false);
  const [modalInclusion, setModalInclusion] = useState<Inclusion | null>(null);

  useEffect(() => {
    scrollToTop();
    navContext.setFooterModePrimary();
    navContext.setNextButtonEnabled(true);
    navContext.setOnNavigateBack(() => {
      // Reset selection when the user goes back
      userContext.setSelectedInclusion(null);
    });

    if (!userContext.selectedInclusion) {
      // If there are 'preselected' inclusions, select that the first one by default
      const preselected = userContext.inclusionsWithPromoPricing.filter(
        (item) => item.preselected
      );
      if (preselected.length > 0) {
        onClickInclusion(preselected[0]);
      }
    }
  }, []);

  const onClickInclusion = (inclusion: Inclusion) => {
    if (
      userContext.selectedInclusion &&
      userContext.selectedInclusion.id === inclusion.id
    ) {
      userContext.setSelectedInclusion(null);
    } else {
      userContext.setSelectedInclusion(inclusion);
    }
  };

  const onClickMoreDetails = (inclusion: Inclusion) => {
    setModalInclusion(inclusion);
    setIsModalVisible(true);
  };

  const onModalClose = () => {
    setIsModalVisible(false);
    setIsModalClosing(false);
  };

  return (
    <div className={"screen screen-personalise-home"}>
      <div className={"container"}>
        <h1>{getPageTitle(location.pathname)}</h1>
        <p className={"subtitle"}>
          Choose from a range of inclusion options to make your new home just
          right for you.
        </p>

        <p className={"section-label"}>Choose your Style</p>
      </div>
      <div className={"inclusions container"}>
        {userContext.inclusionsWithPromoPricing.map((item) => {
          return (
            <CollapsableImageContent
              key={item.id}
              layout={"inclusion"}
              image={item.image}
              title={item.title}
              price={item.price}
              promo_price={item.promo_price}
              selected={
                userContext.selectedInclusion
                  ? userContext.selectedInclusion.id === item.id
                  : false
              }
              onClick={() => onClickInclusion(item)}
              onClickMoreDetails={() => onClickMoreDetails(item)}
              innerHtml={item.content}
            />
          );
        })}
      </div>
      {isModalVisible && modalInclusion !== null && (
        <FullscreenModal
          title={modalInclusion.title}
          closing={isModalClosing}
          onClose={onModalClose}
        >
          <InclusionDetail
            inclusion={modalInclusion}
            onClickBack={() => setIsModalClosing(true)}
          />
        </FullscreenModal>
      )}
    </div>
  );
});

export default PersonaliseHomeScreen;
